import {createDeferred} from '@exadel/esl/modules/esl-utils/async';

const STABILITY_THRESHOLD = 500; // ms
const TIMEOUT = 4000; // ms

export interface IncrementalScrollOptions {
  offset?: number;
  scrollContainer?: HTMLElement;
  stabilityThreshold?: number;
  timeout?: number;
  signal?: AbortSignal;
}

interface IncrementalScrollStrategy {
  calcDistance: (target: HTMLElement | null) => number;
  performScroll: (distance: number) => void;
}

const animationFrames = new WeakMap<HTMLElement | Window, number>();

/** Creates scroll strategy based on provided options */
export function createIncrementalScrollStrategy(
  $el: HTMLElement | null,
  options: IncrementalScrollOptions
): IncrementalScrollStrategy {
  const {scrollContainer, offset = 0} = options;
  if (scrollContainer) {
    return {
      calcDistance: ($target: HTMLElement | null) => {
        if (!$target) return 0;
        const scrollContainerRect = scrollContainer.getBoundingClientRect();
        const targetRect = $target.getBoundingClientRect();
        return targetRect.top - scrollContainerRect.top - offset;
      },
      performScroll: (distance: number) => {
        scrollContainer.scrollTop += distance;
      }
    };
  }
  return {
    calcDistance: ($target: HTMLElement | null) => {
      return $target ? $target.getBoundingClientRect().top - offset : -window.scrollY;
    },
    performScroll: (distance: number) => window.scrollTo(0, window.scrollY + distance)
  };
}

/** Incremental scroll to passed element. Updates target position per each frame. */
export function incrementalScrollTo(
  $el: HTMLElement | null,
  options: IncrementalScrollOptions = {},
  strategy?: IncrementalScrollStrategy
): Promise<void> {
  const deferred = createDeferred<void>();
  let requestId: number;

  const opts = {offset: 0, stabilityThreshold: STABILITY_THRESHOLD, timeout: TIMEOUT, ...options};
  const scrollStrategy = strategy || createIncrementalScrollStrategy($el, opts);
  const scrollContainer = opts.scrollContainer || window;

  let startTime = Date.now();
  let lastUnstableTime = 0;
  const initialTime = startTime;
  let maxStepIncrement = 1;

  function signalCallback(e?: Event): void {
    opts.signal?.removeEventListener('abort', signalCallback);
    if (requestId) cancelAnimationFrame(requestId);
    if (e) rejectOnSignal(); // Only reject if called by abort event
  }
  function rejectOnSignal(): void {
    animationFrames.delete(scrollContainer);
    deferred.reject(new DOMException('Aborted', 'AbortError'));
  }
  opts.signal?.addEventListener('abort', signalCallback);

  const existingRequestId = animationFrames.get(scrollContainer);
  if (existingRequestId) cancelAnimationFrame(existingRequestId);

  requestId = requestAnimationFrame(function step() {
    if (opts.signal?.aborted) return rejectOnSignal();

    const distance = scrollStrategy.calcDistance($el);

    const distanceAbs = Math.abs(distance);
    // The speed of incremental scroll (px/ms) depends on the distance to the target position
    const speed = Math.floor(distanceAbs / 100) + 1;
    // The step increment limits by maxStepIncrement and provides a smooth start to scrolling
    const stepIncrement = Math.min(maxStepIncrement, speed * (Date.now() - startTime));
    startTime = Date.now();

    const increment = Math.floor(Math.min(distanceAbs, stepIncrement));
    const direction = distance > 0 ? increment : -increment;

    scrollStrategy.performScroll(direction);

    if (distanceAbs > 1) {
      lastUnstableTime = startTime;
    }
    if (maxStepIncrement < 1000) maxStepIncrement *= 2;
    if (startTime - lastUnstableTime < opts.stabilityThreshold &&
      startTime - initialTime < opts.timeout) {
      requestId = requestAnimationFrame(step);
      animationFrames.set(scrollContainer, requestId);
    } else {
      opts.signal?.removeEventListener('abort', signalCallback);
      animationFrames.delete(scrollContainer);
      deferred.resolve();
    }
  });
  animationFrames.set(scrollContainer, requestId);
  return deferred.promise;
}
